import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArmchairIcon, CheckIcon, SearchIcon, UserPlusIcon, XIcon } from 'lucide-react';
type Guest = {
  id: number;
  name: string;
  affiliation: string;
  seat: string;
  zone: 'Front row' | 'Press' | 'House';
  arrived: boolean;
};
const seed: Guest[] = [{
  id: 1,
  name: 'Imani Laurent',
  affiliation: 'Édition Mode',
  seat: 'A01',
  zone: 'Front row',
  arrived: true
}, {
  id: 2,
  name: 'Celeste Zhou',
  affiliation: 'VANTA',
  seat: 'A02',
  zone: 'Front row',
  arrived: false
}, {
  id: 3,
  name: 'Theo Mercer',
  affiliation: 'The Ledger',
  seat: 'A03',
  zone: 'Front row',
  arrived: false
}, {
  id: 4,
  name: 'Noor El-Sayed',
  affiliation: 'Collector',
  seat: 'A04',
  zone: 'Front row',
  arrived: true
}, {
  id: 5,
  name: 'Mara Okafor',
  affiliation: 'FORM',
  seat: 'P01',
  zone: 'Press',
  arrived: false
}, {
  id: 6,
  name: 'Louis Kern',
  affiliation: 'Neue Zeitung',
  seat: 'P02',
  zone: 'Press',
  arrived: false
}, {
  id: 7,
  name: 'Anaïs Bell',
  affiliation: 'Maison guest',
  seat: 'B01',
  zone: 'House',
  arrived: true
}, {
  id: 8,
  name: 'Sora Ito',
  affiliation: 'Atelier S',
  seat: 'B02',
  zone: 'House',
  arrived: false
}];
export function VipSeating() {
  const [guests, setGuests] = useState(seed);
  const [zone, setZone] = useState<'All' | Guest['zone']>('All');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Guest | null>(null);
  const visible = useMemo(() => guests.filter(guest => (zone === 'All' || guest.zone === zone) && `${guest.name} ${guest.affiliation} ${guest.seat}`.toLowerCase().includes(query.toLowerCase())), [guests, query, zone]);
  function toggleArrival(id: number) {
    setGuests(current => current.map(guest => guest.id === id ? {
      ...guest,
      arrived: !guest.arrived
    } : guest));
    setSelected(current => current?.id === id ? {
      ...current,
      arrived: !current.arrived
    } : current);
  }
  return <section id="seating" className="border-t border-black py-10" aria-labelledby="seating-title" data-id="element-119">
      <div className="grid gap-6 lg:grid-cols-[1fr_1.5fr] lg:items-end" data-id="element-120">
        <div data-id="element-121">
          <p className="utility-label mb-2" data-id="element-122">06 / GUEST EXPERIENCE</p>
          <h2 id="seating-title" className="editorial-title text-[clamp(3.5rem,8vw,7.5rem)]" data-id="element-123">
            VIP SEATING
          </h2>
          <p className="mt-4 max-w-md font-serif text-xl italic" data-id="element-124">
            Front row follows the runway’s north edge. Keep A05 open until house
            closes.
          </p>
        </div>
        <div data-id="element-125">
          <label className="flex items-center gap-2 border-b border-black pb-2" data-id="element-126">
            <SearchIcon className="h-4 w-4" data-id="element-127" />
            <span className="sr-only" data-id="element-128">Search guest list</span>
            <input value={query} onChange={event => setQuery(event.target.value)} className="w-full bg-transparent outline-none" placeholder="Find guest or seat…" data-id="element-129" />
          </label>
          <div className="mt-4 flex flex-wrap gap-2" data-id="element-130">
            {(['All', 'Front row', 'Press', 'House'] as const).map(item => <button key={item} type="button" onClick={() => setZone(item)} className={`filter-button ${zone === item ? 'filter-button-active' : ''}`} data-id="element-131">
                {item}
              </button>)}
          </div>
        </div>
      </div>

      <div className="mt-8 border-y border-black py-5" data-id="element-132">
        <div className="mb-4 flex items-center gap-3" data-id="element-133">
          <span className="utility-label" data-id="element-134">RUNWAY</span>
          <span className="h-px flex-1 bg-[#c51f2d]" data-id="element-135" />
          <span className="handwritten text-xl text-[#c51f2d]" data-id="element-136">camera →</span>
        </div>
        <motion.div layout className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8" data-id="element-137">
          <AnimatePresence mode="popLayout" data-id="element-138">
            {visible.map(guest => <motion.button layout key={guest.id} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} type="button" onClick={() => setSelected(guest)} className={`focus-ring relative min-h-36 border p-3 text-left transition-colors ${guest.arrived ? 'border-black bg-black text-[#f4efe4]' : 'border-black bg-transparent hover:bg-white/30'}`} data-id="element-139">
                <span className="font-serif text-3xl" data-id="element-140">{guest.seat}</span>
                <span className="mt-6 block text-xs font-bold uppercase leading-tight tracking-[.08em]" data-id="element-141">
                  {guest.name}
                </span>
                <span className="mt-1 block text-[10px] opacity-70" data-id="element-142">
                  {guest.affiliation}
                </span>
                {guest.arrived && <CheckIcon className="absolute right-2 top-2 h-4 w-4 text-[#c51f2d]" data-id="element-143" />}
              </motion.button>)}
          </AnimatePresence>
        </motion.div>
        {!visible.length && <p className="py-12 text-center font-serif text-xl italic" data-id="element-144">
            No guests found in this section.
          </p>}
      </div>

      <AnimatePresence data-id="element-145">
        {selected && <motion.div className="fixed inset-0 z-50 grid place-items-center bg-black/55 p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setSelected(null)} data-id="element-146">
            <motion.div role="dialog" aria-modal="true" initial={{
          scale: 0.96,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.96,
          opacity: 0
        }} className="w-full max-w-sm bg-[#f4efe4] p-7" onClick={event => event.stopPropagation()} data-id="element-147">
              <button type="button" onClick={() => setSelected(null)} className="focus-ring float-right" data-id="element-148">
                <XIcon className="h-5 w-5" data-id="element-149" />
                <span className="sr-only" data-id="element-150">Close guest details</span>
              </button>
              <ArmchairIcon className="h-8 w-8 text-[#c51f2d]" data-id="element-151" />
              <p className="utility-label mt-8" data-id="element-152">
                {selected.zone} / {selected.seat}
              </p>
              <h3 className="mt-2 font-serif text-4xl italic" data-id="element-153">
                {selected.name}
              </h3>
              <p className="mt-2 text-sm" data-id="element-154">{selected.affiliation}</p>
              <button type="button" onClick={() => toggleArrival(selected.id)} className="focus-ring mt-8 flex w-full items-center justify-center gap-2 bg-black px-4 py-3 text-xs font-bold uppercase tracking-[.14em] text-white" data-id="element-155">
                {selected.arrived ? <>
                    <XIcon className="h-4 w-4" data-id="element-156" /> Undo arrival
                  </> : <>
                    <UserPlusIcon className="h-4 w-4" data-id="element-157" /> Check in guest
                  </>}
              </button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}